<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    AudioRecorder,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      attachmentUrl: null,
      attachmentSignedId: null,
      attachmentName: null,
      attachmentType: null,
      uploading: false,
      recording: false,
      recordingPaused: false,
      audioFormat: AUDIO_FORMATS.MP3,
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations() {
    return {
      shortCode: {
        required,
        minLength: minLength(2),
      },
      content: {},
    };
  },
  computed: {
    isImage() {
      return this.attachmentType && this.attachmentType.startsWith('image/');
    },
    isAudio() {
      return this.attachmentType && this.attachmentType.startsWith('audio/');
    },
    isVideo() {
      return this.attachmentType && this.attachmentType.startsWith('video/');
    },
    hasAttachment() {
      return !!this.attachmentUrl;
    },
    isFormValid() {
      return (
        !this.v$.shortCode.$invalid &&
        (this.content.trim().length > 0 || this.hasAttachment)
      );
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachmentUrl = null;
      this.attachmentSignedId = null;
      this.attachmentName = null;
      this.attachmentType = null;
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    async uploadBlob(file, name, type) {
      this.uploading = true;
      const formData = new FormData();
      formData.append('attachment', file);
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        const response = await axios.post(
          `/api/v1/accounts/${accountId}/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        this.attachmentUrl = response.data.file_url;
        this.attachmentSignedId = response.data.blob_id;
        this.attachmentName = name || file.name;
        this.attachmentType = type || file.type;
      } catch (e) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENT.UPLOAD_ERROR'));
      } finally {
        this.uploading = false;
      }
    },
    async handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      await this.uploadBlob(file);
      event.target.value = '';
    },
    startRecording() {
      this.recordingPaused = false;
      this.recording = true;
    },
    stopRecording() {
      if (this.$refs.cannedAudioRecorder) this.$refs.cannedAudioRecorder.stopRecording();
    },
    cancelRecording() {
      this.recordingPaused = false;
      this.recording = false;
    },
    toggleRecordingPause() {
      const recorder = this.$refs.cannedAudioRecorder;
      if (!recorder) return;

      if (this.recordingPaused) recorder.resumeRecording();
      else recorder.pauseRecording();

      this.recordingPaused = !this.recordingPaused;
    },
    async onAudioRecorded(file) {
      this.recording = false;
      this.recordingPaused = false;
      if (file && file.file) {
        await this.uploadBlob(file.file, `gravacao-${Date.now()}.mp3`, 'audio/mp3');
      }
    },
    removeAttachment() {
      this.attachmentUrl = null;
      this.attachmentSignedId = null;
      this.attachmentName = null;
      this.attachmentType = null;
    },
    addCannedResponse() {
      this.addCanned.showLoading = true;
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          attachment_url: this.attachmentUrl,
          attachment_signed_id: this.attachmentSignedId,
        })
        .then(() => {
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
            />
          </div>
        </div>

        <div class="w-full mt-2">
          <label class="mb-1 block">
            {{ $t('CANNED_MGMT.ATTACHMENT.LABEL') }}
          </label>

          <div
            v-if="hasAttachment"
            class="flex items-center gap-3 p-2 border border-n-strong rounded-lg mb-2 bg-n-solid-2"
          >
            <img
              v-if="isImage"
              :src="attachmentUrl"
              class="w-14 h-14 object-cover rounded flex-shrink-0"
              :alt="$t('GENERAL.PREVIEW_ALT')"
            />
            <div
              v-else-if="isAudio"
              class="flex items-center gap-2 flex-1 min-w-0"
            >
              <span class="i-lucide-music text-n-slate-11 flex-shrink-0 size-5" />
              <span class="text-sm text-n-slate-12 truncate">{{ attachmentName }}</span>
            </div>
            <div v-else-if="isVideo" class="flex flex-col gap-2 flex-1 min-w-0">
              <span class="i-lucide-video text-n-slate-11 flex-shrink-0 size-5" />
              <span class="text-sm text-n-slate-12 truncate">{{ attachmentName }}</span>
            </div>
            <div v-else class="flex items-center gap-2 flex-1 min-w-0">
              <span class="i-lucide-file text-n-slate-11 flex-shrink-0 size-5" />
              <span class="text-sm text-n-slate-12 truncate">{{ attachmentName }}</span>
            </div>
            <audio v-if="isAudio" :src="attachmentUrl" controls class="flex-1 max-w-xs" />
            <video v-if="isVideo" :src="attachmentUrl" controls class="flex-1 max-w-xs max-h-40 rounded" />
            <button
              type="button"
              class="ml-auto flex-shrink-0 text-n-slate-11 hover:text-n-ruby-11"
              @click="removeAttachment"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <label
            v-if="!hasAttachment"
            class="flex items-center gap-2 cursor-pointer text-sm text-n-slate-11 hover:text-n-slate-12 px-3 py-2 border border-dashed border-n-strong rounded-lg hover:border-n-strong transition-colors"
          >
            <span v-if="uploading" class="i-lucide-loader-2 animate-spin size-4" />
            <span v-else class="i-lucide-paperclip size-4" />
            <span>{{ uploading ? $t('CANNED_MGMT.ATTACHMENT.UPLOADING') : $t('CANNED_MGMT.ATTACHMENT.PLACEHOLDER') }}</span>
            <input
              type="file"
              class="hidden"
              accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              :disabled="uploading"
              @change="handleFileSelect"
            />
          </label>

          <div v-if="!hasAttachment && !recording" class="mt-2">
            <button
              type="button"
              class="flex items-center gap-2 cursor-pointer text-sm text-n-slate-11 hover:text-n-slate-12 px-3 py-2 border border-dashed border-n-strong rounded-lg transition-colors"
              @click="startRecording"
            >
              <span class="i-lucide-mic size-4" />
              <span>Gravar áudio</span>
            </button>
          </div>
          <div v-if="recording" class="mt-2 p-2 border border-n-strong rounded-lg">
            <AudioRecorder
              ref="cannedAudioRecorder"
              :audio-record-format="audioFormat"
              :is-paused="recordingPaused"
              @finish-record="onAudioRecorded"
              @send="stopRecording"
              @delete="cancelRecording"
              @playpause="toggleRecordingPause"
            />
            <div class="flex gap-2 justify-end mt-2">
              <button type="button" class="text-sm px-3 py-1 border border-n-strong rounded-lg" @click="cancelRecording">Cancelar</button>
              <button type="button" class="text-sm px-3 py-1 bg-n-teal-9 text-white rounded-lg" @click="stopRecording">Parar e anexar</button>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="!isFormValid || addCanned.showLoading || uploading"
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
    @apply min-h-[8rem];

    p {
      @apply text-base;
    }
  }
</style>
